import type { KundaliLang } from "./KundaliFormCard.i18n";
import { isValidOptionalEmail, normalizePhoneE164 } from "@/lib/phone-e164";

export type KundaliFieldErrors = Partial<
  Record<"name" | "birthDate" | "birthTime" | "birthPlace" | "phone" | "email", string>
>;

type ValidationInput = {
  previewBeforeContact?: boolean;
  birthDate: string;
  birthTime: string;
  birthPlace: string;
  language: KundaliLang;
  name: string;
  phone: string;
  email: string;
  placeSelected: boolean;
  requireBirthTime: boolean;
};

type ValidationResult =
  | { errors: KundaliFieldErrors; ok: false; firstField: keyof KundaliFieldErrors }
  | { birthDateIso: string; errors: KundaliFieldErrors; ok: true };

/**
 * Whether the primary submit CTA has the mandatory recurring-identity phone.
 * The single submit button stays the one decision (rule 39); it is disabled
 * until a valid phone is present, with no separate contact-submit step.
 *
 * The phone field emits E.164 (`<PhoneInputE164>`), so validity is "a plausible
 * mobile for the picked country" — India unchanged (10 digits, 6-9), plus the
 * diaspora numbers that were previously rejected outright (ADR-0043).
 */
export function isContactGateSatisfied(phone: string): boolean {
  return normalizePhoneE164(phone) !== null;
}

export function normalizeBirthDateInput(input: string): string | null {
  const raw = input.trim();
  const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (isoMatch) return validIsoDate(isoMatch[1], isoMatch[2], isoMatch[3]);

  const displayMatch = /^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/.exec(raw);
  if (displayMatch) return validIsoDate(displayMatch[3], displayMatch[2], displayMatch[1]);

  const compactMatch = /^(\d{2})(\d{2})(\d{4})$/.exec(raw);
  if (compactMatch) return validIsoDate(compactMatch[3], compactMatch[2], compactMatch[1]);

  return null;
}

/**
 * Inline (as-typed) birth-date validity for the field's own feedback — runs on
 * every keystroke, NOT just at submit. Returns a localized error ONLY once the
 * user has typed a COMPLETE date (10 chars / a full dd/mm/yyyy or yyyy-mm-dd),
 * so we never nag mid-typing. This cuts the 8,684 birthDate failures by telling
 * the seeker the date is impossible (a real calendar date but pre-1900 or in the
 * future, or a non-date like 31/02) the moment they finish typing it, instead of
 * only after they tap submit and lose their place in the form.
 *
 * Returns null while the input is incomplete or valid.
 */
export function birthDateInlineError(input: string, language: KundaliLang): string | null {
  const raw = input.trim();
  // Only judge a fully-typed date. A display value is complete at dd/mm/yyyy
  // (10 chars) or an ISO yyyy-mm-dd; anything shorter is still being typed.
  const isComplete = /^\d{1,2}[./-]\d{1,2}[./-]\d{4}$/.test(raw) || /^\d{4}-\d{2}-\d{2}$/.test(raw);
  if (!isComplete) return null;

  const iso = normalizeBirthDateInput(raw);
  if (!iso) {
    return localized(
      language,
      "That date isn't valid — check the day and month.",
      "Yeh tithi sahi nahi hai — din aur mahina dekhein."
    );
  }
  if (iso < BIRTH_DATE_MIN) {
    return localized(
      language,
      "Birth year must be 1900 or later.",
      "Janm varsh 1900 ya uske baad hona chahiye."
    );
  }
  if (iso > todayIso()) {
    return localized(
      language,
      "Birth date can't be in the future.",
      "Janm tithi bhavishya mein nahi ho sakti."
    );
  }
  return null;
}

export function formatBirthDateDisplayInput(input: string): string {
  const raw = input.trim();
  const isoDisplay = formatIsoBirthDateInput(raw);
  if (isoDisplay) return isoDisplay;

  const separatedDisplay = formatSeparatedBirthDateInput(raw);
  if (separatedDisplay !== null) return separatedDisplay;

  return formatCompactBirthDateDigits(raw);
}

function formatIsoBirthDateInput(raw: string): string | null {
  const isoMatch = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(raw);
  if (isoMatch) {
    return [isoMatch[3].padStart(2, "0"), isoMatch[2].padStart(2, "0"), isoMatch[1]].join("/");
  }
  return null;
}

function formatSeparatedBirthDateInput(raw: string): string | null {
  const slashValue = raw.replace(/[.-]/g, "/");
  if (!slashValue.includes("/")) return null;
  const parts = slashValue.split("/");
  const partialDisplayDate =
    parts.length <= 3 &&
    parts.every((part) => /^\d*$/.test(part)) &&
    parts[0].length <= 2 &&
    (parts[1] ?? "").length <= 2 &&
    (parts[2] ?? "").length <= 4;
  return partialDisplayDate ? parts.join("/") : null;
}

function formatCompactBirthDateDigits(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 8);
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
}

export function validateKundaliForm(input: ValidationInput): ValidationResult {
  const errors: KundaliFieldErrors = {};
  if (!input.name.trim()) {
    errors.name = localized(input.language, "Please enter your name.", "Kripya apna naam likhein.");
  }

  const birthDateIso = normalizeBirthDateInput(input.birthDate);
  if (hasInvalidBirthDate(birthDateIso)) {
    errors.birthDate = localized(
      input.language,
      "Enter birth date as dd/mm/yyyy.",
      "Janm tithi dd/mm/yyyy format mein likhein."
    );
  }

  if (isMissingRequiredBirthTime(input)) {
    errors.birthTime = localized(
      input.language,
      "Enter birth time to start the chat.",
      "Chat ke liye janm samay zaroori hai."
    );
  }

  // Blank vs unresolved are DIFFERENT failures with different corrections: a
  // blank field needs typing; an unresolved one (no gazetteer match, or an
  // ambiguous city whose did-you-mean chip is showing) needs a spelling check
  // or a suggestion tap. Typed cities now resolve on submit (incl. Devanagari),
  // so the old "pick from the dropdown" instruction was misleading.
  if (!input.birthPlace.trim()) {
    errors.birthPlace = localized(
      input.language,
      "Please enter your birth city.",
      "Apna janm sthal likhein."
    );
  } else if (!input.placeSelected) {
    errors.birthPlace = localized(
      input.language,
      "We couldn't confirm that city — check the spelling or tap a suggestion.",
      "Yeh city confirm nahi ho payi — spelling check karein ya suggestion tap karein."
    );
  }

  if (hasInvalidPhone(input)) {
    errors.phone = localized(
      input.language,
      "Enter a valid mobile number for the country you picked.",
      "Chuni hui country ka sahi mobile number daalein."
    );
  }

  if (input.email.trim() && !isValidOptionalEmail(input.email)) {
    errors.email = localized(
      input.language,
      "Enter a valid email, or leave it blank.",
      "Sahi email daalein, ya khaali chhod dein."
    );
  }

  const firstField = firstErrorField(errors);
  if (firstField) return { errors, firstField, ok: false };
  return { birthDateIso: birthDateIso as string, errors, ok: true };
}

function firstErrorField(errors: KundaliFieldErrors): keyof KundaliFieldErrors | null {
  if (errors.name) return "name";
  if (errors.birthDate) return "birthDate";
  if (errors.birthTime) return "birthTime";
  if (errors.birthPlace) return "birthPlace";
  // Phone is deliberately the final required save/reopen step in the shared
  // form. Error focus follows the visible journey instead of jumping from
  // name straight to contact before the chart details (ADR-0043 / rule 69).
  if (errors.phone) return "phone";
  if (errors.email) return "email";
  return null;
}

function hasInvalidPhone(input: ValidationInput): boolean {
  return !input.previewBeforeContact && !isContactGateSatisfied(input.phone);
}

function localized(language: KundaliLang, en: string, hi: string) {
  return language === "hi" ? hi : en;
}

function hasInvalidBirthDate(birthDateIso: string | null) {
  return !birthDateIso || !isAllowedBirthDate(birthDateIso);
}

function isMissingRequiredBirthTime(input: ValidationInput) {
  return input.requireBirthTime && !input.birthTime.trim();
}

function validIsoDate(year: string, month: string, day: string): string | null {
  const yyyy = Number(year);
  const mm = Number(month);
  const dd = Number(day);
  const date = new Date(Date.UTC(yyyy, mm - 1, dd));
  if (date.getUTCFullYear() !== yyyy || date.getUTCMonth() !== mm - 1 || date.getUTCDate() !== dd) {
    return null;
  }
  return `${year.padStart(4, "0")}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
}

// Single source of truth for the allowed birth-date window, shared by the
// submit-time validator and the inline as-typed field check so they can't drift.
export const BIRTH_DATE_MIN = "1900-01-01";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

function isAllowedBirthDate(isoDate: string) {
  return isoDate >= BIRTH_DATE_MIN && isoDate <= todayIso();
}
